import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  IconButton,
  Box,
  Grid,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ClientAutocomplete from "../clientAutocomplete";
import CustomTypography from "../../globalStyles/fonts";

/**
 * "Sync Returns" — pick the client, then kick off the returns backfill on the
 * client's OMS provider (Pipe17 or Acenda — returnService.syncReturns picks).
 *
 * The client is chosen HERE rather than read off a page-level client filter,
 * because this portal's list header does not actually render a client picker
 * (`onSelectClient` is threaded through DashboardContainer/Header but never
 * used) — a button gated on a page-level selection would be permanently
 * disabled for an admin. Same `!localStorage.getItem("client_id")` split every
 * other client-scoped modal uses: a client user is already scoped to their own
 * client and never sees the picker; an admin picks one.
 *
 * The optional date range maps to the provider's `since`/`until`. Left empty it
 * imports the full return history, which is the right default for onboarding —
 * a bounded window is the exception, for re-pulling a period that drifted.
 */
const ReturnSyncModal = ({ open, onClose, onConfirm, syncing }) => {
  const storedClientId = localStorage.getItem("client_id");
  const [selectedClient, setSelectedClient] = useState(null);
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setSelectedClient(null);
      setSince("");
      setUntil("");
      setError("");
    }
  }, [open]);

  const handleConfirm = () => {
    const clientId = storedClientId || selectedClient?._id;
    if (!clientId) {
      setError("Select a client to sync returns for");
      return;
    }
    if (since && until && new Date(since) > new Date(until)) {
      setError("The start date must be on or before the end date");
      return;
    }
    setError("");
    onConfirm({ clientId, since: since || undefined, until: until || undefined });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <DialogTitle sx={{ fontFamily: "Poppins" }}>Sync Returns</DialogTitle>
        <IconButton onClick={onClose} sx={{ mr: 2 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <DialogContent>
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: 12,
            color: "#667085",
            mb: 2,
          }}
        >
          Imports this client&apos;s return history from their OMS provider
          (Pipe17 or Acenda). Returns arrive as they import, so the list may
          take a moment to fill in.
        </Typography>
        <Grid container spacing={2}>
          {!storedClientId && (
            <Grid item xs={12}>
              <CustomTypography>Choose Client</CustomTypography>
              <ClientAutocomplete
                hideLabel
                selectedClient={selectedClient}
                onClientChange={(client) => setSelectedClient(client)}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <CustomTypography>From (optional)</CustomTypography>
            <input
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                fontFamily: "Poppins",
                fontSize: 13,
                border: "1px solid #D0D5DD",
                borderRadius: 4,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography>To (optional)</CustomTypography>
            <input
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                fontFamily: "Poppins",
                fontSize: 13,
                border: "1px solid #D0D5DD",
                borderRadius: 4,
              }}
            />
          </Grid>
        </Grid>
        {error ? (
          <Typography
            sx={{ fontFamily: "Poppins", fontSize: 12, color: "#B42318", mt: 2 }}
          >
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={syncing}
          sx={{ textTransform: "none" }}
          startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {syncing ? "Starting…" : "Start Sync"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReturnSyncModal;
